import CustomLabel from '@/components/custom-label';
import { formatDateTime, handleDownload } from '@/utils/common';
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { App, Button, Col, Form, Row, Select, Table, TableColumnsType, Tabs, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getImportHistoryList, watchImportHistoryList, watchImportHistoryPagination, watchImportHistoryLoading } from '@/redux/slices/import-history.slice';
import { importFoodRecommendation, watchHealthStatusList, resetMessages } from '@/redux/slices/health-status.slice';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';

type ImportRecommendFoodManagementTableProps = {
    setActiveTabProps: React.Dispatch<React.SetStateAction<string>>;
};

type PageChangeParams = {
    page?: number;
    pageSize?: number;
};

export default function ImportRecommendFoodManagementTable({
    setActiveTabProps
}: ImportRecommendFoodManagementTableProps) {
    const [activeTab, setActiveTab] = useState<string>('INPUT');
    const [form] = Form.useForm();
    const [selectedHealthStatus, setSelectedHealthStatus] = useState<string | undefined>(undefined);
    const tabItems = [
        {
            key: 'INPUT',
            label: 'Nhập dữ liệu'
        },
        {
            key: 'HISTORY',
            label: 'Lịch sử nhập dữ liệu'
        }
    ];

    const dispatch = useAppDispatch();
    const importHistoryList = useAppSelector(watchImportHistoryList);
    const pagination = useAppSelector(watchImportHistoryPagination);
    const loading = useAppSelector(watchImportHistoryLoading);
    const healthStatusList = useAppSelector(watchHealthStatusList);

    const { notification } = App.useApp();

    const healthStatusOptions = healthStatusList.map((item) => ({
        value: item.healthStatusId,
        label: item.displayName
    }));

    const tableData = (importHistoryList?.map((item) => ({
        key: item.id.toString(),
        fileName: item.name,
        downloadUrl: item.url,
        inputtedDate: item.createdDate,
        inputUser: item.createdBy,
    })) || [])
    .sort((a, b) => new Date(b.inputtedDate).getTime() - new Date(a.inputtedDate).getTime());

    const columns: TableColumnsType<any> = [
        {
            title: 'STT',
            render: (_, __, index) => (pagination.curPage - 1) * pagination.limitPage + index + 1,
            width: 100
        },
        {
            title: 'Tên file',
            dataIndex: 'fileName',
            key: 'fileName',
        },
        {
            title: 'Tải xuống',
            dataIndex: 'downloadUrl',
            key: 'downloadUrl',
            render: (url) => (
                <div
                    css={css`
                        display: flex;
                        justify-content: center;
                        align-items: center;

                        .ant-btn {
                            border: none;
                        }
                    `}
                >
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={() => window.open(url, '_blank')}
                    />
                </div>
            ),
            width: 100
        },
        {
            title: 'Ngày nhập dữ liệu',
            dataIndex: 'inputtedDate',
            key: 'inputtedDate',
            width: 200,
            render: (record) => (record ? formatDateTime(record) : '')
        },
        {
            title: 'Người nhập',
            dataIndex: 'inputUser',
            key: 'inputUser',
            width: 150
        }
    ];

    const handleUpload = async (file: File, healthStatusId: string) => {
        try {
            
          await dispatch(importFoodRecommendation({ file, healthStatusId })).unwrap();
            notification.info({
                message: 'Nhập dữ liệu thành công!',
                description: 'Dữ liệu đã được nhập thành công.',
                placement: 'topRight',
            });
            setActiveTabProps('MANAGE');
        } catch (error: any) {
            const errorMessage = error?.message || 'Lỗi tình trạng sức khỏe không trùng khớp với file tải lên!';
            notification.error({
                message: errorMessage,
                description: 'Vui lòng kiểm tra lại file dữ liệu.',
                placement: 'topRight',
            });
            console.error('Error importing data:', error);
        }
        dispatch(resetMessages());
        dispatch(getImportHistoryList({ offset: 1, limit: 10, type: 'HEALTH_FOOD_RECOMMENDATION' }));
    };

    const handlePageChange = useCallback(
        ({ page = PAGINATION_PARAMS.offset, pageSize = PAGINATION_PARAMS.limit }: PageChangeParams) => {
            dispatch(
                getImportHistoryList({
                    offset: page,
                    limit: pageSize,
                    type: 'HEALTH_FOOD_RECOMMENDATION'
                })
            );
        },
        [dispatch]
    );

    useEffect(() => {
        handlePageChange({});
    }, [handlePageChange]);

    return (
        <div css={rootStyles}>
            <Row css={boxStyles} align='middle' gutter={16}>
                <Button
                    type='text'
                    icon={<ArrowLeftOutlined />}
                    onClick={() => setActiveTabProps('MANAGE')}
                />
                <Typography.Title level={5} style={{ marginBottom: 0 }}>
                    Nhập theo danh sách khuyến nghị về thực phẩm
                </Typography.Title>
            </Row>

            <Tabs
                css={tabStyle}
                defaultActiveKey='INPUT'
                onChange={(key) => setActiveTab(key)}
            >
                {tabItems.map((item) => (
                    <Tabs.TabPane tab={item.label} key={item.key} />
                ))}
            </Tabs>

            {activeTab === 'INPUT' && (
                <>
                    <div css={boxStyles}>
                        <Form form={form} css={formStyles}>
                            <Row gutter={[24, 16]} align="stretch">
                                <Col span={24}>
                                    <Typography css={{
                                        fontSize: '1.4rem',
                                        lineHeight: '2.4rem',
                                        fontWeight: 400,
                                        fontFamily: 'Roboto',
                                        color: 'rgba(0, 0, 0, 1)',
                                        fontStyle: 'italic'
                                    }}>
                                        (Vui lòng chọn tình trạng sức khỏe để nhập file danh sách khuyến nghị về thực phẩm theo tình trạng sức khỏe.)
                                    </Typography>
                                </Col>
                            </Row>
                            <Row gutter={[24, 16]} align="stretch" css={{ marginTop: '1rem' }}>
                                <Col span={24}>
                                    <Form.Item
                                        label={CustomLabel({ text: 'Tình trạng sức khỏe', isRequired: true })}
                                        name="healthStatus"
                                        css={customLabel}
                                        colon={false}
                                    >
                                        <Select
                                            options={healthStatusOptions}
                                            placeholder="Tình trạng sức khỏe"
                                            onChange={(value) => setSelectedHealthStatus(value)}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                    <div css={boxStyles}>
                        <Row>
                            <Button
                                type='primary'
                                icon={<UploadOutlined />}
                                disabled={!selectedHealthStatus}
                                onClick={() => {
                                    const fileInput = document.createElement('input');
                                    fileInput.type = 'file';
                                    fileInput.accept = '.xlsx, .xls, .csv';
                                    fileInput.onchange = (event) => {
                                        const target = event.target as HTMLInputElement;
                                        if (target.files && target.files[0]) {
                                          handleUpload(target.files[0], selectedHealthStatus as string);
                                        }
                                    };
                                    fileInput.click();
                                }}
                            >
                                Tải lên
                            </Button>
                            <Button type='link'
                              onClick={() => handleDownload(
                                'https://docs.google.com/spreadsheets/d/12q1SE2EXFSdtm51zVG_xG2Rc6uaw7RAL/export?format=xlsx',
                                'PMS_Danh_sach_thuc_pham_nen-han_che_an_theo_tinh_trang_suc_khoe_v1.0.xlsx'
                              )}
                            >
                              Tải xuống template
                            </Button>
                        </Row>

                        <div
                            style={{
                                minHeight: '60vh',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <Typography.Title level={5}>
                                Vui lòng chọn thư mục dữ liệu danh sách thực phẩm
                            </Typography.Title>
                        </div>
                    </div>
                </>
            )}
            {activeTab === 'HISTORY' && (
                <Table<any>
                    columns={columns}
                    dataSource={tableData}
                    pagination={{
                        current: pagination.curPage,
                        pageSize: pagination.limitPage,
                        total: pagination.totalRows,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        onChange: (page, pageSize) => {
                            handlePageChange({ page, pageSize });
                        }
                    }}
                    loading={loading}
                />
            )}
        </div>
    );
}

const rootStyles = css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0 1rem;
    min-height: calc(100vh - var(--header-height));

    .ant-table-thead > tr > th {
        background: var(--blue-dark-color) !important;
        color: var(--white-color);
    }

    .ant-table-cell::before {
        display: none;
    }
`;

const boxStyles = css`
    padding: 1rem;
    background: var(--white-color);
    border-radius: 1rem;
    box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
    &:nth-of-type(2) {
        padding: 1.6rem 1rem;
    }
`;

const tabStyle = css`
    .ant-tabs-nav {
        margin-bottom: 0;
    }

    .ant-tabs-tab {
        padding: 6px 8px;
        color: #333;
        border: 1px solid #ccc;
        background-color: #f0f0f0;
    }

    .ant-tabs-tab-active {
        background-color: var(--blue-dark-color);
        border: 1px solid var(--blue-dark-color);

        .ant-tabs-tab-btn {
            color: #ffffff !important;
        }
    }

    .ant-tabs-ink-bar {
        display: none;
    }

    .ant-tabs-tab:hover {
        color: #333 !important;
    }

    .ant-tabs-tab:not(:first-child) {
        margin: 0 0 0 8px !important;
    }
`;

const customLabel = css`
    .ant-form-item-label > label {
        font-weight: normal !important;
    }

    width: 100%;
    margin-bottom: 0;
`;

const formStyles = css`
    margin-bottom: 1rem;
`;